import React from 'react';
import { Send, Bell, AlertTriangle, Calendar, RotateCcw, Repeat } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAdminPushBroadcast } from '@/hooks/admin/useAdminPushBroadcast';

const AUDIENCE_OPTIONS = [
  { value: 'self', label: 'Just me (test)' },
  { value: 'all', label: 'All users with devices' },
  { value: 'admins', label: 'All admins' },
  { value: 'entity_admins', label: 'All partner admins' },
  { value: 'specific', label: 'Specific user IDs' },
];

const CADENCE_OPTIONS = [
  { value: 'none', label: 'One-time' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
];

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const PushBroadcastCard = React.forwardRef(({ enabled = true, onBroadcastSaved }, ref) => {
  const h = useAdminPushBroadcast({ enabled, onBroadcastSaved });

  // Expose `cloneFrom` and `promoteWeekly` to parent cards.
  React.useImperativeHandle(ref, () => ({
    cloneFrom: (b) => {
      h.cloneFrom(b);
      const node = document.querySelector('[data-testid="push-broadcast-card"]');
      node?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    /**
     * Promote a sent broadcast to a weekly recurrence: clone the content
     * (title/body/audience/deep_link) and pre-set Weekly cadence to today's
     * weekday at 09:00 UTC. Admin can tweak before saving.
     */
    promoteWeekly: (b) => {
      h.cloneFrom(b);
      h.setRecurrenceCadence('weekly');
      h.setRecurrenceTime('09:00');
      // Convert JS getDay() (0=Sun..6=Sat) to our backend (0=Mon..6=Sun).
      const jsDow = new Date().getDay();
      const backendDow = (jsDow + 6) % 7;
      h.toggleRecurrenceDay(backendDow);
      const node = document.querySelector('[data-testid="push-broadcast-card"]');
      node?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
  }));

  const notConfigured = h.preview && h.preview.configured === false;
  const isScheduled = Boolean(h.scheduledFor);
  const isRecurring = h.recurrenceCadence !== 'none';

  return (
    <div
      className="p-4 sm:p-6 rounded-2xl bg-card border border-border/50 space-y-4"
      data-testid="push-broadcast-card"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <Bell className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0">
          <h3 className="text-lg font-semibold leading-tight">Push Notification Broadcast</h3>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Send now, schedule once, or repeat daily / weekly — recipient count updates live.
          </p>
        </div>
      </div>

      {notConfigured && (
        <div
          className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 text-sm"
          data-testid="push-not-configured-banner"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            Firebase Cloud Messaging is not configured on the backend. Set
            <code className="mx-1 px-1 rounded bg-amber-500/20">FIREBASE_SERVICE_ACCOUNT_PATH</code>
            in <code className="px-1 rounded bg-amber-500/20">/app/backend/.env</code> — see{' '}
            <code className="px-1 rounded bg-amber-500/20">FIREBASE_SETUP.md</code>.
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="push-audience">Audience</Label>
          <Select value={h.audience} onValueChange={h.setAudience}>
            <SelectTrigger id="push-audience" data-testid="push-audience-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AUDIENCE_OPTIONS.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  data-testid={`push-audience-option-${opt.value}`}
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Audience size</Label>
          <div
            className="h-10 px-3 rounded-md border border-border/50 bg-muted/30 flex items-center text-sm"
            data-testid="push-audience-preview"
          >
            {h.previewLoading ? (
              <span className="text-muted-foreground">Calculating…</span>
            ) : h.preview ? (
              <span>
                <span className="font-medium" data-testid="push-user-count">
                  {h.preview.user_count}
                </span>{' '}
                user{h.preview.user_count === 1 ? '' : 's'} ·{' '}
                <span className="font-medium" data-testid="push-device-count">
                  {h.preview.device_count}
                </span>{' '}
                device{h.preview.device_count === 1 ? '' : 's'}
              </span>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </div>
        </div>
      </div>

      {h.audience === 'specific' && (
        <div className="space-y-2">
          <Label htmlFor="push-user-ids">User IDs (comma- or space-separated)</Label>
          <Textarea
            id="push-user-ids"
            value={h.specificUserIds}
            onChange={(e) => h.setSpecificUserIds(e.target.value)}
            placeholder="usr_abc123, usr_def456"
            rows={2}
            className="font-mono text-xs"
            data-testid="push-specific-user-ids"
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="push-title">Title</Label>
        <Input
          id="push-title"
          value={h.title}
          onChange={(e) => h.setTitle(e.target.value)}
          placeholder="New badge unlocked!"
          maxLength={200}
          data-testid="push-title-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="push-body">Body</Label>
        <Textarea
          id="push-body"
          value={h.body}
          onChange={(e) => h.setBody(e.target.value)}
          placeholder="Open the app to see your latest achievement."
          rows={3}
          maxLength={500}
          data-testid="push-body-input"
        />
        <p className="text-xs text-muted-foreground text-right">
          {h.body.length}/500
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="push-deep-link">Deep link (optional)</Label>
          <Input
            id="push-deep-link"
            value={h.deepLink}
            onChange={(e) => h.setDeepLink(e.target.value)}
            placeholder="unityblaze://badges or https://…"
            data-testid="push-deep-link-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="push-scheduled-for" className="flex items-center gap-2">
            <Calendar className="h-3.5 w-3.5" />
            Schedule for later (optional)
          </Label>
          <Input
            id="push-scheduled-for"
            type="datetime-local"
            value={h.scheduledFor}
            onChange={(e) => h.setScheduledFor(e.target.value)}
            disabled={isRecurring}
            data-testid="push-scheduled-for-input"
          />
          {isScheduled && (
            <button
              type="button"
              onClick={() => h.setScheduledFor('')}
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              data-testid="push-clear-schedule-btn"
            >
              <RotateCcw className="h-3 w-3" /> Send immediately instead
            </button>
          )}
        </div>
      </div>

      {/* Recurrence */}
      <div
        className="p-3 rounded-xl border border-border/50 bg-muted/20 space-y-3"
        data-testid="push-recurrence-section"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Label className="flex items-center gap-2 text-sm">
            <Repeat className="h-3.5 w-3.5" />
            Repeat
          </Label>
          <div className="flex gap-1.5">
            {CADENCE_OPTIONS.map((opt) => (
              <button
                type="button"
                key={opt.value}
                onClick={() => h.setRecurrenceCadence(opt.value)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  h.recurrenceCadence === opt.value
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card text-muted-foreground border-border/50 hover:text-foreground'
                }`}
                data-testid={`push-cadence-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {isRecurring && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="push-recurrence-time" className="text-xs">Fire at (UTC)</Label>
              <Input
                id="push-recurrence-time"
                type="time"
                value={h.recurrenceTime}
                onChange={(e) => h.setRecurrenceTime(e.target.value)}
                data-testid="push-recurrence-time"
              />
            </div>
            {h.recurrenceCadence === 'weekly' && (
              <div className="space-y-1.5">
                <Label className="text-xs">Days</Label>
                <div className="flex gap-1 flex-wrap" data-testid="push-recurrence-days">
                  {WEEKDAY_LABELS.map((lbl, idx) => {
                    const active = h.recurrenceDays.includes(idx);
                    return (
                      <button
                        type="button"
                        key={idx}
                        onClick={() => h.toggleRecurrenceDay(idx)}
                        className={`text-[11px] w-9 h-7 rounded-md border transition-colors ${
                          active
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-card text-muted-foreground border-border/50 hover:text-foreground'
                        }`}
                        data-testid={`push-recurrence-day-${idx}`}
                      >
                        {lbl}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {h.lastReport && !isScheduled && !isRecurring && (
        <div
          className="p-3 rounded-xl bg-muted/40 border border-border/50 text-sm space-y-1"
          data-testid="push-delivery-report"
        >
          <p className="font-medium">Last delivery report</p>
          {h.lastReport.error ? (
            <p className="text-destructive" data-testid="push-report-error">
              {h.lastReport.error}
            </p>
          ) : (
            <p className="text-muted-foreground">
              sent <span className="text-foreground font-medium">{h.lastReport.sent ?? 0}</span> ·
              failed <span className="text-foreground font-medium">{h.lastReport.failed ?? 0}</span> ·
              pruned <span className="text-foreground font-medium">{h.lastReport.pruned ?? 0}</span> ·
              audience <span className="text-foreground font-medium">{h.lastReport.audience_size ?? 0}</span>
            </p>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <Button
          onClick={h.sendOrSchedule}
          disabled={h.sending || !h.title.trim() || !h.body.trim()}
          data-testid="push-send-btn"
        >
          {isRecurring ? (
            <>
              <Repeat className="h-4 w-4 mr-2" />
              {h.sending ? 'Saving…' : 'Save recurring'}
            </>
          ) : isScheduled ? (
            <>
              <Calendar className="h-4 w-4 mr-2" />
              {h.sending ? 'Scheduling…' : 'Schedule push'}
            </>
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              {h.sending ? 'Sending…' : 'Send push'}
            </>
          )}
        </Button>
      </div>
    </div>
  );
});

PushBroadcastCard.displayName = 'PushBroadcastCard';

export default PushBroadcastCard;
